import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Alert,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SimpleNotesInput from '../UI/SimpleNotesInput';
import usePhotoCapture from '../../hooks/usePhotoCapture';
import { auth } from '../../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import useQuickCheckInRoutingState from '../../hooks/useQuickCheckInRoutingState';
import useQuickCheckInSaveFlow from '../../hooks/useQuickCheckInSaveFlow';
import { coerceCalendarDate } from '../../utils/calendarDateKey';
import { isDebugFlagEnabled } from '../../utils/debugTools';
import LogSheetTitle from '../UI/LogSheetTitle';
import QuickCheckInActionFooter from './QuickCheckInActionFooter';
import QuickCheckInCaptureFields from './QuickCheckInCaptureFields';
import QuickCheckInCapturePreview from './QuickCheckInCapturePreview';
import QuickTagsSection from './QuickTagsSection';
import CareNoteComposerSurface from '../Dashboard/shared/CareNoteComposerSurface';
import VoiceCaptureDialog from '../../features/voiceCapture/VoiceCaptureDialog';
import VoiceAttachmentPreview from '../../features/voiceCapture/VoiceAttachmentPreview';
import { buildVoiceNoteDisplayName } from '../../features/voiceCapture/voiceDisplayName';
import { IMAGE_CAPTURE_INTENTS, normalizeImageCaptureIntent } from '../../features/imageCapture/imageCaptureIntent';
import { buildImageDisplayName } from '../../features/imageCapture/imageDisplayName';

export {
  getQuickLogPreAiDecision,
  getQuickLogRoutingPreview,
  shouldUseQuickLogSmartCapture,
} from '../../utils/quickLogRoutingDecision';
export {
  getQuickLogRoutingDecision,
  getQuickNoteCaptureState,
  shouldEscalateQuickStructuredReviewToAi,
} from '../../utils/quickLogRoutingDecision';
export {
  buildCanonicalQuickNoteSavePayload,
  buildCanonicalStructuredQuickNoteSaveEntry,
} from '../../utils/quickLogSavePayload';
export {
  resolveQuickLogLongNoteRows,
  summarizeBackendQuickLogResponse,
} from '../../utils/quickLogReviewHelpers';

const getTimeInputValue = (date = new Date()) =>
  new Date(date).toTimeString().slice(0, 5);

const isQuickLogRoutingDebugEnabled = () => {
  return isDebugFlagEnabled(['debugQuickLogRouting', 'debugImportParser']);
};

const isQuickNoteCategoryAuditEnabled = () => {
  return isDebugFlagEnabled('debugNaturalCaptureAudit');
};

const QuickCheckIn = ({
  child,
  onComplete,
  onSkip,
  onConvertToEntries,
  onAiCaptureStart,
  onAiCaptureEnd,
  onAiCaptureError,
  onImportLogs,
  onForwardEmail,
  onOpenSleepLog,
  onOpenFoodLog,
  onOpenMedicalLog,
  onOpenBathroomLog,
  onOpenIncident,
  open = true,
  mode = 'create',
  initialData = null,
  initialDate = null,
  initialAction = null,
  initialPhotoFile = null,
  initialPhotoIntent = '',
  initialText = '',
  onCaptureDateChange = null,
  entryId = null,
  sheetTitle = 'Quick Note',
  embedded = false,
  showHeader = true,
  showCapturePreviewSection = true,
  showTagSection = true,
  showDebugDetails = true,
  overrideCategory = null,
  autoSubmitOnOpen = false,
  headless = false,
}) => {
  const [user] = useAuthState(auth);
  const isEditMode = mode === 'edit' || Boolean(entryId || initialData?.id);

  const [noteData, setNoteData] = useState({
    text: '',
    mediaFile: null,
    audioBlob: null,
  });
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [selectedTime, setSelectedTime] = useState(() => getTimeInputValue());
  const [selectedTags, setSelectedTags] = useState([]);
  const [importantMoment, setImportantMoment] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoIntent, setPhotoIntent] = useState(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false);
  const photoInputRef = useRef(null);
  const pendingPhotoIntentRef = useRef('');
  const consumedInitialActionRef = useRef(null);
  const {
    cameraOpen,
    cameraError,
    videoRef,
    canvasRef,
    handleCameraClick,
    handleFlipCamera,
    capturePhoto,
    closeCamera,
  } = usePhotoCapture({
    fileNamePrefix: 'quick-note-photo',
    onCapture: (file) => {
      if (file) {
        setPhotoFile(file);
        setPhotoIntent(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
      }
    },
    errorMessage: 'Could not access the camera. Try taking a photo from your library instead.',
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    if (isEditMode && initialData) {
      const nextTimestamp = initialData.timestamp?.toDate
        ? initialData.timestamp.toDate()
        : (initialData.timestamp ? new Date(initialData.timestamp) : null);
      const nextDate = coerceCalendarDate(initialData.localDate || nextTimestamp || new Date());
      const nextTime = initialData.localTime
        || (nextTimestamp instanceof Date && !Number.isNaN(nextTimestamp.getTime())
          ? getTimeInputValue(nextTimestamp)
          : getTimeInputValue());

      if (nextDate) {
        setSelectedDate(nextDate);
      }
      if (nextTime) {
        setSelectedTime(nextTime);
      }

      setNoteData({
        text: initialData.text
          || initialData.noteContextText
          || initialData.sourceText
          || initialData.originalData?.sourceText
          || initialData.note
          || initialData.content
          || initialData.description
          || initialData.title
          || initialData.label
          || initialData.source?.body
          || initialData.originalData?.source?.body
          || '',
        mediaFile: null,
        audioBlob: null,
      });
      setPhotoFile(null);
      setPhotoPreviewUrl('');
      setSelectedTags(Array.isArray(initialData.tags) ? initialData.tags : []);
      setImportantMoment(Boolean(initialData.importantMoment));
      return;
    }

    const nextCreateDate = coerceCalendarDate(initialDate || new Date()) || new Date();
    setSelectedDate(nextCreateDate);
    setSelectedTime(getTimeInputValue(nextCreateDate));
    setNoteData({ text: String(initialText || '').trim(), mediaFile: null, audioBlob: null, voiceTranscript: '' });
    setPhotoFile(initialPhotoFile || null);
    setPhotoIntent(normalizeImageCaptureIntent(initialPhotoIntent));
    setPhotoPreviewUrl('');
    closeCamera();
    setSelectedTags([]);
    setImportantMoment(false);
  }, [closeCamera, open, initialData, initialDate, initialPhotoFile, initialPhotoIntent, initialText, isEditMode]);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(photoFile);
    setPhotoPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [photoFile]);

  useEffect(() => {
    if (!open) {
      consumedInitialActionRef.current = null;
      return undefined;
    }

    if (!initialAction || consumedInitialActionRef.current === `${child?.id || 'child'}:${initialAction}`) {
      return undefined;
    }

    consumedInitialActionRef.current = `${child?.id || 'child'}:${initialAction}`;
    const timer = window.setTimeout(() => {
      if (initialAction === 'add_photo') {
        photoInputRef.current?.click();
      }
      if (initialAction === 'take_photo') {
        handleCameraClick();
      }
      if (initialAction === 'record_voice') {
        setVoiceDialogOpen(true);
      }
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [child?.id, handleCameraClick, initialAction, open]);

  const {
    noteText,
    quickCaptureTranscript,
    quickCaptureOriginalText,
    quickCaptureNormalizedText,
    quickNoteCategoryDecision,
    quickCaptureStructureDecision,
    quickLogRoutingDecision,
    quickLogRoutingPreview,
    resolvedCategory,
    detectedQuickNoteEntities,
    detectedQuickNoteLabels,
    shouldShowFrontendCapturePreview,
    shouldShowBackendSmartCapturePreview,
    shouldShowReviewNeededPreview,
    notePlaceholder,
    hasNoteText,
  } = useQuickCheckInRoutingState({
    noteData,
    selectedTags,
    overrideCategory,
    showCapturePreviewSection,
  });
  const isQuickLogRoutingDebugMode = useMemo(() => isQuickLogRoutingDebugEnabled(), []);
  const isQuickNoteCategoryAuditMode = useMemo(() => isQuickNoteCategoryAuditEnabled(), []);

  const combinedTags = useMemo(() => selectedTags, [selectedTags]);

  const handlePhotoSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setPhotoFile(file);
    setPhotoIntent(pendingPhotoIntentRef.current || IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
    pendingPhotoIntentRef.current = '';
    event.target.value = '';
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoIntent(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
    pendingPhotoIntentRef.current = '';
    setPhotoPreviewUrl('');
    closeCamera();
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const handleAddPhoto = () => {
    pendingPhotoIntentRef.current = IMAGE_CAPTURE_INTENTS.SAVE_PHOTO;
    setPhotoIntent(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
    photoInputRef.current?.click();
  };

  const handleTakePhotoForTimeline = () => {
    pendingPhotoIntentRef.current = IMAGE_CAPTURE_INTENTS.SAVE_PHOTO;
    setPhotoIntent(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
    handleCameraClick();
  };

  const handleUploadImage = (event) => {
    event?.stopPropagation?.();
    handleAddPhoto();
  };

  const handleUploadFile = (event) => {
    event?.stopPropagation?.();
    onSkip?.();
    window.setTimeout(() => {
      onImportLogs?.(child, null, { uploadKind: 'file', captureDate: selectedDate });
    }, 0);
  };

  const voiceDisplayName = buildVoiceNoteDisplayName({ date: selectedDate, time: selectedTime });
  const photoDisplayName = buildImageDisplayName({ date: selectedDate, time: selectedTime });

  const handleVoiceReady = ({ audioBlob, transcript, displayName, originalFileName, mimeType, transcription }) => {
    setNoteData((current) => ({
      ...current,
      voiceTranscript: String(transcript || '').trim(),
      mediaFile: null,
      audioBlob,
      audioFileName: displayName || voiceDisplayName,
      audioOriginalFileName: originalFileName || '',
      audioMimeType: mimeType,
      transcription,
    }));
  };

  const hasAttachedMedia = Boolean(photoFile || noteData?.audioBlob);
  const {
    saving,
    saveError,
    quickLogDebugPayload,
    handlePrimaryAction,
  } = useQuickCheckInSaveFlow({
    child,
    onComplete,
    onConvertToEntries,
    onAiCaptureStart,
    onAiCaptureEnd,
    onAiCaptureError,
    isEditMode,
    entryId,
    initialData,
    noteData,
    setNoteData,
    selectedDate,
    selectedTime,
    importantMoment,
    user,
    photoFile,
    photoIntent,
    removePhoto,
    combinedTags,
    quickCaptureOriginalText,
    quickCaptureNormalizedText,
    quickCaptureTranscript,
    quickCaptureStructureDecision,
    quickNoteCategoryDecision,
    quickLogRoutingDecision,
    quickLogRoutingPreview,
    resolvedCategory,
    detectedQuickNoteEntities,
    isQuickLogRoutingDebugMode,
    isQuickNoteCategoryAuditMode,
    noteText,
    hasNoteText,
    hasAttachedMedia,
    headless,
    autoSubmitOnOpen,
    open,
  });
  const primaryActionLabel = saving
    ? 'Saving...'
    : isEditMode
      ? 'Save note'
      : 'Send';
  const primaryActionIcon = saving
    ? <CircularProgress size={16} color="inherit" />
    : <SendRoundedIcon />;
  const actionHelperText = saving
    ? 'Saving your capture...'
    : 'Photo, voice, and note send together.';

  if (headless) {
    return null;
  }

  const selectedDateTime = new Date(selectedDate);
  const [selectedHours = '0', selectedMinutes = '0'] = String(selectedTime || '').split(':');
  selectedDateTime.setHours(Number(selectedHours) || 0, Number(selectedMinutes) || 0, 0, 0);

  const handleSurfaceDateTimeChange = (nextDateTime) => {
    if (!(nextDateTime instanceof Date) || Number.isNaN(nextDateTime.getTime())) {
      return;
    }

    setSelectedDate(nextDateTime);
    setSelectedTime(getTimeInputValue(nextDateTime));
    onCaptureDateChange?.(nextDateTime);
  };

  const openStructuredLogFromComposer = (handler) => {
    if (typeof handler !== 'function') {
      return null;
    }

    return (targetChild = child) => {
      onSkip?.();
      window.setTimeout(() => {
        handler(targetChild || child);
      }, 0);
    };
  };

  if (embedded && !showHeader && !showCapturePreviewSection && !showTagSection) {
    return (
      <>
      <CareNoteComposerSurface
        child={child}
        dataCy="quick-check-in-sheet"
        flush
        sx={{
          px: 0,
          pt: 0,
          pb: 0,
        }}
        contentSx={{
          px: { xs: 1.5, sm: 1.85 },
          pb: 'max(14px, env(safe-area-inset-bottom))',
        }}
        captureDate={selectedDateTime}
        onCaptureDateChange={handleSurfaceDateTimeChange}
        composerInputRef={null}
        composerText={noteData.text}
        onComposerFocus={() => {}}
        onComposerTextChange={(event) => setNoteData({
          ...noteData,
          text: event.target.value,
          mediaFile: null,
        })}
        composerSaving={saving}
        composerSavingLabel={saving ? 'Saving...' : primaryActionLabel}
        composerSavingHelper="Saving your note and organizing care details..."
        composerError={saveError}
        hasComposerContent={hasNoteText || hasAttachedMedia}
        onContinue={handlePrimaryAction}
        onDismiss={onSkip}
        onOpenSleepLog={openStructuredLogFromComposer(onOpenSleepLog)}
        onOpenFoodLog={openStructuredLogFromComposer(onOpenFoodLog)}
        onOpenMedicalLog={openStructuredLogFromComposer(onOpenMedicalLog)}
        onOpenBathroomLog={openStructuredLogFromComposer(onOpenBathroomLog)}
        onOpenIncident={openStructuredLogFromComposer(onOpenIncident)}
        onTakePhoto={handleTakePhotoForTimeline}
        onUploadImage={handleUploadImage}
        onUploadFile={handleUploadFile}
        onRecordVoice={() => setVoiceDialogOpen(true)}
        onForwardEmail={onForwardEmail ? (event) => {
          event?.stopPropagation?.();
          onSkip?.();
          window.setTimeout(() => onForwardEmail(child), 0);
        } : null}
        composerAudioBlob={noteData.audioBlob}
        composerAudioFileName={noteData.audioFileName}
        onRemoveVoice={() => setNoteData((current) => ({
          ...current,
          mediaFile: null,
          audioBlob: null,
          voiceTranscript: '',
        }))}
        photoInputRef={photoInputRef}
        onPhotoSelect={handlePhotoSelect}
        composerPhotoFile={photoFile}
        composerPhotoDisplayName={photoDisplayName}
        composerPhotoPreviewUrl={photoPreviewUrl}
        onRemovePhoto={removePhoto}
        composerPhotoIntent={photoIntent}
        onComposerPhotoIntentChange={setPhotoIntent}
        cameraOpen={cameraOpen}
        cameraError={cameraError}
        onCloseCamera={closeCamera}
        onFlipCamera={handleFlipCamera}
        videoRef={videoRef}
        onCapturePhoto={capturePhoto}
        canvasRef={canvasRef}
      />
      <VoiceCaptureDialog open={voiceDialogOpen} child={child} displayName={voiceDisplayName} onClose={() => setVoiceDialogOpen(false)} onReady={handleVoiceReady} />
      </>
    );
  }

  return (
    <>
    <Card
      elevation={0}
      sx={{
        border: embedded ? 'none' : '1px solid #e4dcca',
        borderRadius: embedded ? 0 : { xs: '28px', md: '32px' },
        backgroundColor: embedded ? 'transparent' : '#ffffff',
        boxShadow: embedded ? 'none' : '0 18px 50px rgba(15, 23, 42, 0.08)',
        position: 'relative',
        overflow: embedded ? 'visible' : 'hidden',
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2.25, md: 2.5 } }}>
        {showHeader ? (
          <LogSheetTitle
            title={sheetTitle}
            titleBadge={child.name}
            onClose={onSkip}
            compactTitle
          />
        ) : null}

        {isQuickLogRoutingDebugMode && showDebugDetails && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              alignSelf: 'flex-start',
              mt: -0.5,
              mb: 1,
              px: 1,
              py: 0.45,
              borderRadius: '999px',
              bgcolor: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #fca5a5',
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Debug build
          </Box>
        )}

        <QuickCheckInCaptureFields
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          onSelectedDateChange={setSelectedDate}
          onSelectedTimeChange={setSelectedTime}
          photoInputRef={photoInputRef}
          photoFile={photoFile}
          photoDisplayName={photoDisplayName}
          photoPreviewUrl={photoPreviewUrl}
          onPhotoSelect={handlePhotoSelect}
          onRemovePhoto={removePhoto}
          photoIntent={photoIntent}
          onPhotoIntentChange={setPhotoIntent}
          cameraOpen={cameraOpen}
          cameraError={cameraError}
          videoRef={videoRef}
          canvasRef={canvasRef}
          onCameraClick={handleCameraClick}
          onFlipCamera={handleFlipCamera}
          onCapturePhoto={capturePhoto}
          onRecordVoice={() => setVoiceDialogOpen(true)}
        >
          <Box sx={{ mt: 2 }}>
            <SimpleNotesInput
              value={noteData}
              placeholder={notePlaceholder}
              onDataChange={(nextData) => setNoteData((current) => ({
                ...current,
                ...nextData,
                audioBlob: current.audioBlob || nextData.audioBlob,
              }))}
              hintText="Tip: use your phone mic to dictate notes."
              minRows={4}
            />
            <VoiceAttachmentPreview
              audioBlob={noteData.audioBlob}
              fileName={noteData.audioFileName}
              onRemove={() => setNoteData((current) => ({
                ...current,
                mediaFile: null,
                audioBlob: null,
                voiceTranscript: '',
              }))}
            />
            {saving ? (
              <Box
                role="status"
                aria-live="polite"
                sx={{
                  mt: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: '#5B6A63',
                }}
              >
                <CircularProgress size={14} color="inherit" />
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, lineHeight: 1.35 }}>
                  Saving your note and organizing care details...
                </Typography>
              </Box>
            ) : null}
          </Box>
        </QuickCheckInCaptureFields>

        <QuickCheckInCapturePreview
          showCapturePreviewSection={showCapturePreviewSection}
          hasNoteText={hasNoteText}
          shouldShowFrontendCapturePreview={shouldShowFrontendCapturePreview}
          shouldShowReviewNeededPreview={shouldShowReviewNeededPreview}
          shouldShowBackendSmartCapturePreview={shouldShowBackendSmartCapturePreview}
          detectedQuickNoteLabels={detectedQuickNoteLabels}
          detectedQuickNoteEntities={detectedQuickNoteEntities}
          isDebugMode={isQuickLogRoutingDebugMode}
          showDebugDetails={showDebugDetails}
          quickLogRoutingPreview={quickLogRoutingPreview}
          shouldUseBackendExtraction={quickLogRoutingDecision.shouldUseBackendExtraction}
          quickLogDebugPayload={quickLogDebugPayload}
        />

        {saveError ? (
          <Alert severity="warning" sx={{ mt: 1.25 }}>
            {saveError}
          </Alert>
        ) : null}

        <QuickTagsSection
          showTagSection={showTagSection}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          importantMoment={importantMoment}
          setImportantMoment={setImportantMoment}
          userId={user?.uid}
        />

        <QuickCheckInActionFooter
          helperText={actionHelperText}
          primaryActionLabel={primaryActionLabel}
          primaryActionIcon={primaryActionIcon}
          onPrimaryAction={handlePrimaryAction}
          disabled={(!hasNoteText && !hasAttachedMedia) || saving}
        />
      </CardContent>

    </Card>
    <VoiceCaptureDialog open={voiceDialogOpen} child={child} displayName={voiceDisplayName} onClose={() => setVoiceDialogOpen(false)} onReady={handleVoiceReady} />
    </>
  );
};

export default QuickCheckIn;
